import { ref, Ref, reactive, computed, watch, toRefs } from '@vue/composition-api';
import { Route } from 'vue-router';

import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import { useMutation } from 'vue-query';
import { ApiCacheContext } from '@services/ApiCacheContext';
import useErrors from '@composables/useErrors';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import { ROUTE_NAME } from '@/router/route-name';
import { notNil, ValueTypeOf } from '@berlin-front/common/utils';
import { AUTHCODE_RESEND_MESSAGE, ATONE_SMS_DESCRIPTION_MESSAGE, UseSmsAuthContextType } from './context';

type AuthPageOptions = {
  /**
   * 次ページのRouteName
   */
  nextPage: ValueTypeOf<typeof ROUTE_NAME>;
  /**
   * 前ページのRouteName
   */
  previousPage: ValueTypeOf<typeof ROUTE_NAME>;
};

const useSendAuthCode = (): {
  isLoading: Ref<boolean>;
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
  send: (code: string) => void;
  reset: Ref<() => void>;
} => {
  const { sms, tracks } = useContext(BerlinBffContext);
  const { atoneAuthModalParameter, tokens, atoneTracksPostResult, setAtoneTracksTrustResult } =
    useContext(ApiCacheContext);

  const {
    mutate: sendVerify,
    isLoading: isVerifyLoading,
    isSuccess: isVerifySucceeded,
    isError: isVerifyError,
    reset: resetSmsVerify,
  } = useMutation((smsToken: string) =>
    sms.atoneSmsControllerSmsVerifyPost(notNil(tokens.value.npToken), notNil(atoneAuthModalParameter.value.pubKey), {
      smsToken,
      trackToken: notNil(atoneTracksPostResult.value?.trackToken),
    }),
  );

  const {
    mutate: trackTokenUpdate,
    isLoading: isTrackTokenUpdateLoading,
    isSuccess: isTrackTokenUpdateSucceeded,
    isError: isTrackTokenUpdateError,
    reset: resetTrackTokenUpdate,
  } = useMutation(
    () =>
      tracks.atoneTracksControllerTrackTokenUpdate(
        notNil(atoneTracksPostResult.value?.trackToken),
        notNil(tokens.value.npToken),
        notNil(atoneAuthModalParameter.value.pubKey),
        { trustedFlag: true },
      ),
    {
      onSuccess() {
        setAtoneTracksTrustResult({ trustedFlag: true });
      },
    },
  );

  // ベリファイ完了したら自動的にトラックトークンをアップデートしてtrustedにする。
  watch(isVerifySucceeded, () => {
    if (isVerifySucceeded.value) {
      trackTokenUpdate();
    }
  });

  const isLoading = useOrAll(isVerifyLoading, isTrackTokenUpdateLoading);
  const isSuccess = isTrackTokenUpdateSucceeded;
  const isError = useOrAll(isTrackTokenUpdateError, isVerifyError);
  const reset = computed(() => {
    const handlers = [resetSmsVerify.value, resetTrackTokenUpdate.value];
    return () => handlers.forEach((handler) => handler());
  });

  return { isLoading, isSuccess, isError, send: sendVerify, reset };
};

const useResendSmsCode = (
  infoMessages: Ref<string[]>,
): {
  isLoading: Ref<boolean>;
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
  send: () => void;
  reset: Ref<() => void>;
} => {
  const { sms } = useContext(BerlinBffContext);
  const { atoneAuthModalParameter, setAtoneSmsNotifyPostResult, atoneTracksPostResult, tokens } =
    useContext(ApiCacheContext);

  const {
    mutate: send,
    isLoading,
    isSuccess,
    isError,
    reset,
  } = useMutation(
    () =>
      sms.atoneSmsControllerSmsNotifyPost(notNil(tokens.value.npToken), notNil(atoneAuthModalParameter.value.pubKey), {
        resend: true,
        trackToken: notNil(atoneTracksPostResult.value?.trackToken),
      }),
    {
      onSuccess({ data }) {
        setAtoneSmsNotifyPostResult(data);
        infoMessages.value = [...infoMessages.value, AUTHCODE_RESEND_MESSAGE];
      },
    },
  );
  return { isLoading, isSuccess, isError, send, reset };
};

export const useSmsAuth =
  (props: AuthPageOptions) =>
  (_$route: Route): UseSmsAuthContextType => ({
    useSmsAuth: (_authCodeRef: Ref): ReturnType<UseSmsAuthContextType['useSmsAuth']> => {
      const router = useRouter();
      const { errors, clearAll } = useErrors();
      const infoMessages = ref<string[]>([]);

      const { atoneSmsNotifyPostResult, atoneAuthenticateGetResult } = useContext(ApiCacheContext);

      const {
        //
        send: auth,
        isLoading: isAuthLoading,
        reset: resetAuthRef,
        isError: isAuthErrorRef,
        isSuccess: isAuthSucceededRef,
      } = useSendAuthCode();

      const {
        send: resendCode,
        isLoading: isResendSmsLoading,
        reset: resetResendCodeRef,
        isError: isResendCodeErrorRef,
        isSuccess: isResendCodeSucceeded,
      } = useResendSmsCode(infoMessages);

      const phoneNumber = computed(() =>
        notNil(atoneSmsNotifyPostResult.value?.phoneNumber || atoneAuthenticateGetResult.value?.signInId || ''),
      );

      const isLoading = computed(() => isAuthLoading.value || isResendSmsLoading.value);
      const isEnded = computed(
        () =>
          !isLoading.value &&
          [isAuthErrorRef, isAuthSucceededRef, isResendCodeErrorRef, isResendCodeSucceeded].some((v) => v.value),
      );
      const isInitialState = computed(() => !isLoading.value && !isEnded.value);

      const {
        progress,
        completed: progressCompleted,
        isLoading: isLoadingApi,
      } = toRefs(useFakeProgress(5000, isLoading, isEnded));

      const onSubmitVerify = (smsCode: string) => {
        if (progressCompleted.value || isInitialState.value) {
          resetAuthRef.value?.();
          resetResendCodeRef.value?.();
          clearAll();
          infoMessages.value = [];
          auth(smsCode);
        }
      };

      const onSubmitResend = () => {
        if (progressCompleted.value || isInitialState.value) {
          resetAuthRef.value?.();
          resetResendCodeRef.value?.();
          clearAll();
          infoMessages.value = [];
          resendCode();
        }
      };

      const backToPrevious = () => {
        void router.replace({ name: props.previousPage });
      };

      watch([isAuthSucceededRef, progressCompleted] as const, () => {
        if (isAuthSucceededRef.value && progressCompleted.value) {
          void router.replace({
            name: props.nextPage,
          });
        }
      });

      return reactive({
        description: ATONE_SMS_DESCRIPTION_MESSAGE,
        phoneNumber,
        errorMessages: computed(() => Object.values(errors.value ?? {}).flat()),
        infoMessages,
        progress,
        isLoadingApi,
        onSubmitVerify,
        onSubmitResend,
        backToRegister: undefined,
        backToPrevious,
      });
    },
  });
